export type TransactionTypes =
  | 'on_ramp_top_up'
  | 'external_deposit'
  | 'external_withdraw'
  | 'kem_to_kem'
  | 'on_ramp_send'
  | 'kem_reward'
  | 'card_write_off'
  | 'saving_transfer_in'
  | 'saving_transfer_out'
  | 'swap_sell'
  | 'swap_convert'
  | 'boxo_purchase'

export type TransactionDirectionTypes = 'send' | 'receive'

export type TransactionStateTypes =
  | 'completed'
  | 'processing'
  | 'declined'
  | 'cancelled'
  | 'refunded'

export type RewardTypes = 'referral' | 'signup'

export const normalizeText = (text: string): string => {
  return text?.toLowerCase()?.trim() || ''
}

export const getTransactionConditionalsToRenderLayouts = ({
  direction,
  type,
  state,
  transactionLinkInExplorer,
  rewardType,
}: {
  direction?: TransactionDirectionTypes
  type?: TransactionTypes
  state?: TransactionStateTypes
  transactionLinkInExplorer?: string
  rewardType?: RewardTypes
}) => {
  const directionNormalized = normalizeText(
    direction || '',
  ) as TransactionDirectionTypes
  const stateNormalized = normalizeText(state || '') as TransactionStateTypes
  const typeNormalized = normalizeText(type || '') as TransactionTypes
  const rewardTypeNormalized = normalizeText(
    rewardType || '',
  ) as Lowercase<RewardTypes>

  const isSend = directionNormalized === 'send'
  const isReceive = directionNormalized === 'receive'
  const isCardPurchase = typeNormalized === 'card_write_off'
  const isBoxoPurchase = typeNormalized === 'boxo_purchase'
  const isSell = typeNormalized === 'swap_sell'
  const isPurchase = typeNormalized === 'on_ramp_top_up'
  const isWithdrew = typeNormalized === 'external_withdraw'
  const isDeposit = typeNormalized === 'external_deposit'
  const isConvert = typeNormalized === 'swap_convert'
  const isItMoneyOutflow =
    isSend || typeNormalized === 'external_withdraw' || isSell || isCardPurchase || isBoxoPurchase
  const isKemToKem =
    typeNormalized === 'kem_to_kem' || typeNormalized === 'on_ramp_send'
  const isFailed =
    stateNormalized === 'declined' || stateNormalized === 'cancelled'
  const isRefunded = stateNormalized === 'refunded'
  const isCompleted = stateNormalized === 'completed'
  const isPending = stateNormalized === 'processing'
  const isSavingsTransferIn = typeNormalized === 'saving_transfer_in'
  const isSavingsTransferOut = typeNormalized === 'saving_transfer_out'

  const isReferralReward =
    typeNormalized === 'kem_reward' && rewardTypeNormalized === 'referral'
  const isSignUpReward =
    typeNormalized === 'kem_reward' && rewardTypeNormalized === 'signup'

  const canRenderSubtitle = !isFailed && isSell
  const canRenderTotalDescription =
    !isFailed &&
    (isPurchase ||
      isCardPurchase ||
      isBoxoPurchase ||
      isWithdrew ||
      isReferralReward ||
      isSignUpReward)
  const transactionTypesWithHash =
    isDeposit || isWithdrew || isPurchase || isKemToKem
  const canRenderViewOnBlockchainExplorer =
    !!transactionLinkInExplorer && transactionTypesWithHash

  return {
    isItMoneyOutflow,
    isKemToKem,
    isRefunded,
    isFailed,
    isCompleted,
    isPending,
    isPurchase,
    isSell,
    isSend,
    isReceive,
    isDeposit,
    isConvert,
    isWithdrew,
    canRenderSubtitle,
    canRenderTotalDescription,
    canRenderViewOnBlockchainExplorer,
    isReferralReward,
    isSignUpReward,
    isCardPurchase,
    isBoxoPurchase,
    isSavingsTransferIn,
    isSavingsTransferOut,
  }
}

// Utility to generate random ID
export const generateRandomId = (): string => {
  return Math.random().toString(36).substr(2, 9)
}

// Utility to capitalize first letter
export const capitalizeFirstLetter = (text: string): string => {
  if (!text) return text
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Utility to format date by device region
export const formatDateByDeviceRegion = ({
  date,
  isTitle = false,
  showTime = false,
  locale,
}: {
  date: Date | string
  isTitle?: boolean
  showTime?: boolean
  locale?: string
}): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: isTitle ? 'long' : '2-digit',
    day: '2-digit',
  }

  if (showTime) {
    options.hour = '2-digit'
    options.minute = '2-digit'
  }

  // Use provided locale or fallback to en-US
  return new Intl.DateTimeFormat(locale || 'en-US', options).format(dateObj)
}

export const transactionUtils = {
  getTransactionConditionalsToRenderLayouts,
  generateRandomId,
  capitalizeFirstLetter,
  formatDateByDeviceRegion,
}
